import { normalize, schema } from 'normalizr';
import { fromJS } from 'immutable';
import { combineReducers } from 'redux-immutable';
import { LOAD_NEW_STATE } from '../actions';
import onscreenReducer from './onscreen';
import warningsReducer from './warnings';
import uiReducer from './ui';
import assembledDocumentsReducer from './assembledDocuments';
import { emptyMap } from '../utils/emptiness';

const noOpReducer = (state) => {
  return typeof state === 'undefined' ? emptyMap : state;
};

const defaultUi = {
  working: false,
  saveAndContinueModalShowing: false,
  isMakingSnapshot: false,
  snapshotStatusMessage: '',
};

const combinedReducer = combineReducers({
  onscreen: onscreenReducer,
  warnings: warningsReducer,
  ui: uiReducer,

  block: noOpReducer,
  choices: noOpReducer,
  fields: noOpReducer,
  session: noOpReducer,
  blockId: noOpReducer,
  sessionId: noOpReducer,
  variables: noOpReducer,
  presignedS3Urls: noOpReducer,
  assembledDocuments: assembledDocumentsReducer,
  markdownAttachments: noOpReducer,
  wordAttachments: noOpReducer,
  pdfAttachments: noOpReducer,
  fileAttachments: noOpReducer,
  fieldIdsByVariableName: noOpReducer,
  styles: noOpReducer,
  activated: noOpReducer,
  selectedCurrency: noOpReducer,
  selectedLanguage: noOpReducer,
  saveAndContinueEnabled: noOpReducer,
  clientPortalEndpoint: noOpReducer,
  includeClAttribution: noOpReducer,
  loopVariableInProgress: noOpReducer,
  translations: noOpReducer,
});

const loadNewState = (oldState, action) => {
  const choiceSchema = new schema.Entity('choices');
  const fieldSchema = new schema.Entity('fields', {
    choices: [choiceSchema],
  });
  const markdownAttachmentsSchema = new schema.Entity('markdownAttachments');
  const fileAttachmentsSchema = new schema.Entity('fileAttachments');
  const wordAttachmentsSchema = new schema.Entity('wordAttachments');
  const pdfAttachmentsSchema = new schema.Entity('pdfAttachments');
  const blockSchema = new schema.Entity('block', {
    fields: [fieldSchema],
    markdownAttachments: [markdownAttachmentsSchema],
    wordAttachments: [wordAttachmentsSchema],
    pdfAttachments: [pdfAttachmentsSchema],
    fileAttachments: [fileAttachmentsSchema],
  });
  const sessionSchema = new schema.Entity('session', {
    block: blockSchema,
  });

  const incomingState = action.state || {};
  const normalizedData = normalize(incomingState, sessionSchema).entities;
  normalizedData.sessionId = action.state.id;
  normalizedData.blockId = action.state.block.id;
  normalizedData.onscreen = {};
  normalizedData.warnings = {};
  normalizedData.ui = defaultUi;
  normalizedData.ui.hasWebsocketConnection = oldState.get('ui').get('hasWebsocketConnection');
  normalizedData.variables = action.state.variables;
  normalizedData.presignedS3Urls = action.state.presignedS3Urls;
  normalizedData.assembledDocuments = action.state.assembledDocuments;
  normalizedData.styles = action.state.styles;
  normalizedData.activated = action.state.activated;
  normalizedData.selectedCurrency = action.state.selected_currency;
  normalizedData.selectedLanguage = action.state.selected_language;
  normalizedData.translations = JSON.parse(action.state.translations);
  normalizedData.saveAndContinueEnabled = action.state.save_and_continue_enabled;
  normalizedData.clientPortalEndpoint = action.state.client_portal_endpoint;
  normalizedData.includeClAttribution = action.state.include_cl_attribution;
  normalizedData.loopVariableInProgress = action.state.loop_variable_in_progress;
  normalizedData.fieldIdsByVariableName = {};

  Object.values(normalizedData.fields || {}).forEach((field) => {
    normalizedData.onscreen[field.immutableVariableName] = field.default;
    normalizedData.fieldIdsByVariableName[field.immutableVariableName] = field.id;
  });

  return fromJS(normalizedData);
};

const rootReducer = (state, action) => {
  switch (action.type) {
    case LOAD_NEW_STATE:
      return loadNewState(state || emptyMap, action);
    default:
      return combinedReducer(state || emptyMap, action);
  }
};

export default rootReducer;
